import { unref, computed } from 'vue'
import encoding from 'encoding-japanese'
import moment from 'moment'

/**
 * 文字列をUTF8からSJISのUint8Arrayに変換する
 * @param {String} text 変換するUTF8テキスト
 * @returns {Uint8Array}
 */
const convertUtf8ToSjisUint8Array = (text) => {
  const unicodeArray = []
  for (let i = 0; i < text.length; ++i) {
    unicodeArray.push(text.charCodeAt(i))
  }

  const sjisArray = encoding.convert(unicodeArray, {
    from: 'UNICODE',
    to: 'SJIS',
  })
  return new Uint8Array(sjisArray)
}

/**
 * ユーザーリストのCSVエクスポート機能の利用
 * @param {MaybeRef.<GridApi>} gridApi グリッド操作API
 * @param {RefLike.<ColDef[]>} columnDefs カラム定義
 * @param {RefLike.<ColumnState[]>} columnStates カラム状態
 * @param {MaybeRef.<Date>} updateDate データ更新日時
 * @param {RefLike.<boolean>} isSomeConditionSet 何らかの絞り込み条件が設定されている場合はtrue
 */
export const useUserListCsv = ({
  gridApi,
  columnDefs,
  columnStates,
  updateDate,
  isSomeConditionSet,
}) => {
  /**
   * カラムの表示状態
   * @type {ComputedRef.<Map.<string, boolean>>}
   */
  const _columnVisibleMap = computed(
    () =>
      new Map(unref(columnStates).map((state) => [state.colId, !state.hide]))
  )
  /**
   * カラムの並び順
   * @type {ComputedRef.<Map.<string, number>>}
   */
  const _columnIndexMap = computed(
    () =>
      new Map(unref(columnStates).map((state, index) => [state.colId, index]))
  )
  /**
   * CSV出力するカラムID
   * @type {ComputedRef.<string[]>}
   */
  const columnKeys = computed(() =>
    unref(columnDefs)
      .map((c) => c.colId)
      .filter((id) => _columnVisibleMap.value.get(id))
      .sort(
        (a, b) => _columnIndexMap.value.get(a) - _columnIndexMap.value.get(b)
      )
  )

  /**
   * CSVエクスポート時に使用するパラメータ
   * @type {ComputedRef.<CsvExportParams>}
   */
  const parameter = computed(() => {
    return {
      suppressQuotes: true,
      columnKeys: columnKeys.value,
      processHeaderCallback: ({ column }) =>
        `"${column.getColDef().headerName}"`,
      processCellCallback: ({ value, formatValue, column }) => {
        if (value == null) return ''

        const cellDataType = column.getColDef().cellDataType
        switch (cellDataType) {
          case 'number': {
            return value
          }
          case 'attributeNumber': {
            const formattedValue = formatValue(value)
            return formattedValue === '-' ? '' : formattedValue
          }
          default: {
            const formattedValue = formatValue(value)
            return formattedValue === '-' ? '' : `"${formattedValue}"`
          }
        }
      },
    }
  })

  /**
   * CSVをダウンロードする
   * @param {boolean} onlySelected 選択中の行データのみダウンロードする場合はtrue
   */
  const download = (onlySelected = false) => {
    const rawCsv = unref(gridApi)?.getDataAsCsv({
      ...parameter.value,
      onlySelected,
    })
    const sjisCsv = convertUtf8ToSjisUint8Array(rawCsv)
    const blob = new Blob([sjisCsv], { type: 'text/csv' })
    const anchor = document.createElement('a')
    anchor.href = window.URL.createObjectURL(blob)
    anchor.download = _generateCsvFilename(onlySelected)
    anchor.click()
  }

  /**
   * ダウンロードするCSVのファイル名を生成する
   * @param {boolean} onlySelected 選択中の行データのみダウンロードする場合はtrue
   */
  const _generateCsvFilename = (onlySelected) => {
    let suffix
    if (onlySelected) {
      suffix = 'selected'
    } else {
      suffix = unref(isSomeConditionSet) ? 'filtered' : 'all'
    }
    const words = [
      'userlist',
      moment(unref(updateDate)).format('YYYYMMDDHHmm'),
      suffix,
    ]
    return `${words.join('_')}.csv`
  }

  return {
    parameter,
    download,
  }
}
